// Funções puras de domínio para a página Pintura & Lacagem.
// Sem JSX, sem efeitos colaterais.
// Estrutura espelhada de Terceiros/helpers.js para manter consistência visual
// e arquitetural entre as duas telas de "ambientes parados em terceiro estágio".

import { diasUteis } from "../../utils/calculos";
import { hasValue, toDisplayText } from "../../utils/data";
import { F } from "../../utils/gerencialFields";
import { buildDeadlineStatus, compareDatesAsc } from "../../utils/status";
import {
  ACABAMENTO_ITEMS,
  dataEntradaAcabamento,
  isAmbienteEmAcabamento,
  listarItensPendentesAcabamento,
  prazoAcabamento,
} from "../../utils/acabamento";

const EMPTY_VALUE = "—";

export function statusDoPrazo(prazo) {
  if (!prazo) {
    return { status: "Sem prazo", statusVariant: "warning" };
  }
  return buildDeadlineStatus(prazo, {
    overdueLabel: "Prazo vencido",
    todayLabel: "Vence hoje",
    warningLabel: "Vence essa semana",
    fallbackLabel: "No prazo",
  });
}

export function buildLinhas(dados, hoje = new Date()) {
  const linhas = [];

  dados.forEach((row, index) => {
    if (!isAmbienteEmAcabamento(row)) return;

    const itens = listarItensPendentesAcabamento(row);
    if (itens.length === 0) return;

    const entrada = dataEntradaAcabamento(row);
    const diasParado = entrada ? diasUteis(entrada, hoje) : null;
    const prazo = prazoAcabamento(row);
    const statusInfo = statusDoPrazo(prazo);

    itens.forEach((item, itemIndex) => {
      linhas.push({
        key: `${index}-${item.id}`,
        ambienteIndex: index,
        cliente: toDisplayText(row[F.NOME_DO_CLIENTE], "Cliente não informado"),
        ambiente: toDisplayText(row[F.AMBIENTE], "Ambiente não informado"),
        numeroLote: toDisplayText(row[F.NUMERO_DO_LOTE], EMPTY_VALUE),
        item,
        descricao: hasValue(item.descricao) ? item.descricao : EMPTY_VALUE,
        entrada,
        diasParado,
        prazo,
        valor: row[F.VALOR_DO_AMBIENTE],
        primeiroDoAmbiente: itemIndex === 0,
        row,
        ...statusInfo,
      });
    });
  });

  linhas.sort((left, right) => {
    const byPrazo = compareDatesAsc(left.prazo, right.prazo);
    if (byPrazo !== 0) return byPrazo;
    const byDias = (right.diasParado ?? -1) - (left.diasParado ?? -1);
    if (byDias !== 0) return byDias;
    return left.ambienteIndex - right.ambienteIndex;
  });

  return linhas;
}

export function buildAmbientesAgrupados(linhas) {
  const map = new Map();
  linhas.forEach((linha) => {
    let entry = map.get(linha.ambienteIndex);
    if (!entry) {
      entry = {
        ambienteIndex: linha.ambienteIndex,
        cliente: linha.cliente,
        ambiente: linha.ambiente,
        numeroLote: linha.numeroLote,
        entrada: linha.entrada,
        diasParado: linha.diasParado,
        prazo: linha.prazo,
        status: linha.status,
        statusVariant: linha.statusVariant,
        valor: linha.valor,
        row: linha.row,
        itens: [],
      };
      map.set(linha.ambienteIndex, entry);
    }
    entry.itens.push(linha);
  });
  return [...map.values()];
}

export function buildBreakdownPorTipo(linhas) {
  // Estrutura: { itemId: { id, label, total, vencidos, hoje, semana, naoVencidos } }
  const accum = new Map();
  ACABAMENTO_ITEMS.forEach((item) => {
    accum.set(item.id, {
      id: item.id,
      label: item.label,
      total: 0,
      vencidos: 0,
      hoje: 0,
      semana: 0,
      naoVencidos: 0,
    });
  });

  linhas.forEach((linha) => {
    const entry = accum.get(linha.item.id);
    if (!entry) return;
    entry.total += 1;
    if (linha.statusVariant === "danger") entry.vencidos += 1;
    else if (linha.statusVariant === "today") entry.hoje += 1;
    else if (linha.statusVariant === "warning") entry.semana += 1;
    else entry.naoVencidos += 1;
  });

  return ACABAMENTO_ITEMS.map((item) => accum.get(item.id));
}

export function breakdownTone(entry) {
  if (!entry || entry.total === 0) return "default";
  if (entry.vencidos > 0) return "danger";
  if (entry.hoje > 0) return "today";
  if (entry.semana > 0) return "warning";
  return "success";
}

function countAmbientesAtrasados(linhas) {
  return new Set(
    linhas.filter((linha) => linha.statusVariant === "danger").map((l) => l.ambienteIndex)
  ).size;
}

export function buildNextAction(linhas) {
  if (!linhas || linhas.length === 0) {
    return {
      tone: "default",
      title: "Sem itens em Pintura & Lacagem",
      body: "Nenhum ambiente está aguardando finalização de acabamento.",
      ctaLabel: null,
      cta: null,
    };
  }

  const vencidos = linhas.filter((l) => l.statusVariant === "danger");
  if (vencidos.length > 0) {
    const ambientes = countAmbientesAtrasados(linhas);
    return {
      tone: "danger",
      title: `${vencidos.length} ${vencidos.length === 1 ? "item vencido" : "itens vencidos"}`,
      body: `Em ${ambientes} ${ambientes === 1 ? "ambiente" : "ambientes"} — priorizar finalização do acabamento.`,
      ctaLabel: "Ver vencidos",
      cta: { kind: "filter-status", value: "Prazo vencido" },
    };
  }

  const hoje = linhas.filter((l) => l.statusVariant === "today");
  if (hoje.length > 0) {
    return {
      tone: "today",
      title: `${hoje.length} ${hoje.length === 1 ? "item vencendo hoje" : "itens vencendo hoje"}`,
      body: "Confirmar finalização no dia.",
      ctaLabel: "Ver de hoje",
      cta: { kind: "filter-status", value: "Vence hoje" },
    };
  }

  const semana = linhas.filter((l) => l.statusVariant === "warning" && l.status !== "Sem prazo");
  if (semana.length > 0) {
    return {
      tone: "warning",
      title: `${semana.length} ${semana.length === 1 ? "item vence" : "itens vencem"} essa semana`,
      body: "Antecipar finalização.",
      ctaLabel: "Ver da semana",
      cta: { kind: "filter-status", value: "Vence essa semana" },
    };
  }

  const semPrazoParados = linhas.filter(
    (l) => l.status === "Sem prazo" && (l.diasParado ?? 0) > 15
  );
  if (semPrazoParados.length > 0) {
    const ambientes = new Set(semPrazoParados.map((l) => l.ambienteIndex)).size;
    return {
      tone: "warning",
      title: `${ambientes} ${ambientes === 1 ? "ambiente parado" : "ambientes parados"} há mais de 15 du sem prazo`,
      body: "Definir prazo ou priorizar finalização.",
      ctaLabel: "Ver sem prazo",
      cta: { kind: "filter-status", value: "Sem prazo" },
    };
  }

  return {
    tone: "success",
    title: "Nenhuma ação crítica",
    body: "Todos os itens estão dentro do prazo.",
    ctaLabel: null,
    cta: null,
  };
}

function normalize(value) {
  return String(value ?? "").toLowerCase();
}

export function matchSearch(linha, query) {
  if (!query) return true;
  const q = normalize(query);
  return [
    linha.cliente,
    linha.ambiente,
    linha.numeroLote,
    linha.item?.label,
    linha.descricao,
  ]
    .map(normalize)
    .some((text) => text.includes(q));
}

export function applyFilters(linhas, { query, statuses, tipos }) {
  const trimmedQuery = (query ?? "").trim();
  const hasStatusFilter = statuses && statuses.size > 0;
  const hasTipoFilter = tipos && tipos.size > 0;

  return linhas.filter((linha) => {
    if (trimmedQuery && !matchSearch(linha, trimmedQuery)) return false;
    if (hasStatusFilter && !statuses.has(linha.status)) return false;
    if (hasTipoFilter && !tipos.has(linha.item.id)) return false;
    return true;
  });
}

export function reapplyPrimeiroDoAmbiente(linhas) {
  // Após filtragem, recalcula `primeiroDoAmbiente` para preservar a hierarquia
  // visual da coluna Cliente/Ambiente.
  const seen = new Set();
  return linhas.map((linha) => {
    if (seen.has(linha.ambienteIndex)) {
      return { ...linha, primeiroDoAmbiente: false };
    }
    seen.add(linha.ambienteIndex);
    return { ...linha, primeiroDoAmbiente: true };
  });
}

export function averageDiasParados(linhas) {
  if (!linhas || linhas.length === 0) return 0;
  // Calcula por ambiente único — não inflate quando 1 ambiente tem 3 itens.
  const porAmbiente = new Map();
  linhas.forEach((linha) => {
    if (linha.diasParado == null) return;
    porAmbiente.set(linha.ambienteIndex, linha.diasParado);
  });
  if (porAmbiente.size === 0) return 0;
  const total = [...porAmbiente.values()].reduce((sum, dias) => sum + dias, 0);
  return Math.round(total / porAmbiente.size);
}

export const STATUS_FILTER_OPTIONS = [
  { value: "Prazo vencido", tone: "danger" },
  { value: "Vence hoje", tone: "today" },
  { value: "Vence essa semana", tone: "warning" },
  { value: "No prazo", tone: "success" },
  { value: "Sem prazo", tone: "default" },
];
